import { Button } from "@/components/ui/baseUi/button"
import { appToast as toast, warningToastClassName } from "@/components/ui/baseUi/sonner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/baseUi/tabs"
import { UserProfileEntity } from "@/modules/users/entities/usersEntity"
import AnatomyIcon from "@images/svgIcons/profile/anatomyIcon"
import BookIcon from "@images/svgIcons/profile/bookIcon"
import CrossFitIcon from "@images/svgIcons/profile/crossfitIcon"
import HomeIcon from "@images/svgIcons/profile/homeIcon"
import IdentityIcon from "@images/svgIcons/profile/identityIcon"
import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage, isFormFieldInValid } from "@modules/formik/formikUtils"
import { AnatomyForm } from "@modules/profile/myProfile/components/profileAnatomyForm"
import { BiographyForm } from "@modules/profile/myProfile/components/profileBiographyForm"
import { ContactForm } from "@modules/profile/myProfile/components/profileContactForm"
import { CrossfitInformationForm } from "@modules/profile/myProfile/components/profileCrossfitInformationForm"
import { ProfileIdentifyForm } from "@modules/profile/myProfile/components/profileIdentifyForm"
import { useProfileFormik } from "@modules/profile/myProfile/hooks/useProfileFormik"
import { getValidationSchema, type ProfileFormTabProps } from "@modules/profile/myProfile/profileFormikType"
import { Loader, LoaderCircle } from "lucide-react"
import { Suspense } from "react"

type ProfileFormProps = {
  user: UserProfileEntity
}

export function ProfileForm({ user }: ProfileFormProps) {
  const { formik, isSubmitting, activeIndex, setActiveIndex } = useProfileFormik(user)

  const props: ProfileFormTabProps = {
    isFormFieldInValid: (input) => isFormFieldInValid(input, formik),
    getFormErrorMessage: (input, errorMessage) => getFormErrorMessage(input, formik, { errorMessage }),
    formik: formik,
  }

  const items: { index: string; label: string; icon: React.ReactNode; content: React.ReactNode }[] = [
    {
      index: "0",
      label: word("profile.form.identity.tabLabel"),
      icon: <IdentityIcon color="currentColor" className="mr-1 size-6" />,
      content: <ProfileIdentifyForm {...props} />,
    },
    {
      index: "1",
      label: word("profile.form.crossfit.tabLabel"),
      icon: <CrossFitIcon color="currentColor" className="mr-1 size-6" />,
      content: <CrossfitInformationForm {...props} />,
    },
    {
      index: "2",
      label: word("profile.form.biography.tabLabel"),
      icon: <BookIcon color="currentColor" className="mr-1 size-6" />,
      content: <BiographyForm {...props} />,
    },
    {
      index: "3",
      label: word("profile.form.contact.tabLabel"),
      icon: <HomeIcon color="currentColor" className="mr-1 size-6" />,
      content: <ContactForm {...props} />,
    },
    {
      index: "4",
      label: word("profile.form.anatomy.tabLabel"),
      icon: <AnatomyIcon color="currentColor" className="mr-1 size-6" />,
      content: <AnatomyForm {...props} />,
    },
  ]

  const selectTab = (index: string) => {
    getValidationSchema(activeIndex)
      .validate(formik.values, { abortEarly: false })
      .then(() => setActiveIndex(index))
      .catch((err) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        toast.warning(err.inner[0].message, {
          classNames: warningToastClassName(),
        })
      })
  }

  return (
    <form className="flex w-full grow flex-col justify-between gap-8">
      <Tabs
        defaultValue={activeIndex.toString()}
        onValueChange={(value) => {
          selectTab(value)
        }}
        value={activeIndex.toString()}
        className="relative flex w-full flex-col"
      >
        <TabsList variant="profile" className="w-full">
          {items.map((item, index) => (
            <TabsTrigger variant="profile" value={item.index} className="h-auto" key={index}>
              {item.icon}
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {items.map((item, index) => (
          <TabsContent value={item.index} className="flex w-full" key={index}>
            <Suspense fallback={<Loader className="animate-spin pt-18" />}>{item.content}</Suspense>
          </TabsContent>
        ))}
      </Tabs>

      {/* Submit button */}
      <Button
        variant="primary"
        size="lg"
        onClick={() => void formik.submitForm()}
        disabled={isSubmitting || Object.keys(formik.errors).length > 0}
        className="mt-2 self-end"
        type="button"
        isLoading={isSubmitting}
      >
        {isSubmitting && <LoaderCircle className="animate-spin" />}
        {word("global.save")}
      </Button>
    </form>
  )
}
